// EPSE Sites Hook
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { EPSESite } from '@/types/epse';
import { toast } from 'sonner';

export function useEPSESites() {
  const queryClient = useQueryClient();
  
  const { data: sites, isLoading, error } = useQuery({
    queryKey: ['epse-sites'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('epse_sites')
        .select(`
          *,
          organization:org_id (
            id,
            name
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as unknown as EPSESite[];
    },
  });
  
  const createSite = useMutation({
    mutationFn: async (site: Partial<EPSESite>) => {
      const { data: user } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('epse_sites')
        .insert({
          name: site.name || 'Nieuwe Site',
          connection_kw_limit: site.connection_kw_limit || 100,
          org_id: site.org_id || null,
          annual_kwh_total: site.annual_kwh_total || null,
          pv_installed_kwp: site.pv_installed_kwp || null,
          tags: site.tags || [],
          tenant_id: user.user?.id,
          created_by: user.user?.id,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epse-sites'] });
      toast.success('Site aangemaakt');
    },
    onError: (error) => {
      toast.error(`Fout bij aanmaken: ${error.message}`);
    },
  });
  
  const updateSite = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<EPSESite> & { id: string }) => {
      const { name, connection_kw_limit, org_id, annual_kwh_total, pv_installed_kwp, tags } = updates;
      const { data, error } = await supabase
        .from('epse_sites')
        .update({ name, connection_kw_limit, org_id, annual_kwh_total, pv_installed_kwp, tags })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epse-sites'] });
      toast.success('Site bijgewerkt');
    },
    onError: (error) => {
      toast.error(`Fout bij bijwerken: ${error.message}`);
    },
  });
  
  const deleteSite = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('epse_sites')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epse-sites'] });
      toast.success('Site verwijderd');
    },
    onError: (error) => {
      toast.error(`Fout bij verwijderen: ${error.message}`);
    },
  });
  
  return {
    sites,
    isLoading,
    error,
    createSite,
    updateSite,
    deleteSite,
  };
}

export function useEPSESite(siteId: string | undefined) {
  return useQuery({
    queryKey: ['epse-site', siteId],
    queryFn: async () => {
      if (!siteId) return null;
      
      const { data, error } = await supabase
        .from('epse_sites')
        .select(`
          *,
          organization:org_id (
            id,
            name
          )
        `)
        .eq('id', siteId)
        .single();
      
      if (error) throw error;
      return data as unknown as EPSESite;
    },
    enabled: !!siteId,
  });
}
